/**
 * ScenarioPicker — dev/testing UI for flipping between rendered brief scenarios.
 * (BEACON-34 Phase 2.5)
 *
 * The engine generates fixtures at multiple weather scenarios:
 *   01-calm-day, 02-breezy-day, 03-hot-day, 04-stormy-day, 05-coldwindy-day
 *
 * sync-briefs.sh copies them all into pwa/public/briefs/<slug>/scenarios/<id>.html
 * and writes a manifest.json listing what's available. This picker reads that
 * manifest, builds a <select>, and on change calls briefLoader.loadUrl().
 *
 * Hidden by default. Reveals only after a successful manifest fetch + at least
 * 2 scenarios available (no point showing a single-option picker).
 *
 * Phase 3+ removal candidate: once live weather drives the engine on demand,
 * the static-scenario concept goes away and this picker can be retired or
 * repurposed (e.g., "what-if my round was tomorrow?").
 */

export class ScenarioPicker {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.toolbar - container that holds the picker UI (starts hidden)
   * @param {HTMLSelectElement} opts.select - the <select> element to populate
   * @param {string} opts.courseSlug - e.g. "crandon-golf"
   * @param {(url: string) => Promise<any>} opts.onChange - callback fired with full URL on selection change
   */
  constructor({ toolbar, select, courseSlug, onChange }) {
    if (!toolbar || !select) throw new Error("ScenarioPicker: toolbar + select required");
    if (!courseSlug) throw new Error("ScenarioPicker: courseSlug required");
    if (typeof onChange !== "function") throw new Error("ScenarioPicker: onChange callback required");

    this.toolbar = toolbar;
    this.select = select;
    this.courseSlug = courseSlug;
    this.onChange = onChange;
    this.manifest = null;
  }

  async init() {
    const manifestUrl = `/briefs/${this.courseSlug}/manifest.json`;
    try {
      const resp = await fetch(manifestUrl, { cache: "no-cache" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      this.manifest = await resp.json();
    } catch (err) {
      console.warn(`[ScenarioPicker] manifest fetch failed (picker stays hidden):`, err);
      return;
    }

    const scenarios = this.manifest?.scenarios || [];
    if (scenarios.length < 2) {
      console.info(`[ScenarioPicker] only ${scenarios.length} scenario(s) — picker stays hidden`);
      return;
    }

    // Populate options. "latest" is the canonical default; named scenarios follow.
    this.select.innerHTML = "";
    const latestOpt = document.createElement("option");
    latestOpt.value = `/briefs/${this.courseSlug}/latest.html`;
    latestOpt.textContent = `Latest (= ${this.manifest.default_scenario || "default"})`;
    latestOpt.dataset.role = "latest";
    this.select.appendChild(latestOpt);

    for (const id of scenarios) {
      const opt = document.createElement("option");
      opt.value = `/briefs/${this.courseSlug}/scenarios/${id}.html`;
      opt.textContent = humanize(id);
      opt.dataset.scenarioId = id;
      this.select.appendChild(opt);
    }

    this.select.addEventListener("change", (e) => {
      const url = e.target.value;
      console.info(`[ScenarioPicker] switching to ${url}`);
      this.onChange(url).catch((err) => {
        console.error("[ScenarioPicker] load failed:", err);
      });
    });

    this.toolbar.hidden = false;
    console.info(`[ScenarioPicker] ready — ${scenarios.length} scenario(s) available`);
  }
}

/** "01-calm-day" → "Calm day" */
function humanize(id) {
  return id
    .replace(/^\d+-/, "") // strip leading "NN-"
    .replace(/-/g, " ")
    .replace(/^\w/, (c) => c.toUpperCase());
}
